import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import { api, useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Gamepad2, Brain, Trophy, Clock, Calendar,
  Play, X, Settings, School, CheckCircle, XCircle, Medal, Star,
  BookOpen, Search, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

// New AI-Ready Games Configuration
const GAMES = [
  {
    id: 'ai-context-word-finder',
    name: 'Context Word Finder',
    description: 'Find missing words using context clues - 4 difficulty levels',
    icon: Search,
    color: 'cyan',
    bgColor: 'bg-cyan-500/10',
    iconColor: 'text-cyan-400',
    path: '/games/ai-context-word-finder/index.html',
    features: ['TTS Support', 'Leaderboard', 'Offline', 'Levels']
  },
  {
    id: 'ai-picture-match',
    name: 'Picture Match',
    description: 'Match pictures with words - Visual learning',
    icon: Brain,
    color: 'emerald',
    bgColor: 'bg-emerald-500/10',
    iconColor: 'text-emerald-400',
    path: '/games/ai-picture-match/index.html',
    features: ['TTS Support', 'Leaderboard', 'Offline']
  },
  {
    id: 'ai-spelling-bee',
    name: 'Spelling Bee',
    description: 'Listen and spell - Progressive difficulty levels',
    icon: BookOpen,
    color: 'amber',
    bgColor: 'bg-amber-500/10',
    iconColor: 'text-amber-400',
    path: '/games/ai-spelling-bee/index.html',
    features: ['TTS Support', 'Leaderboard', 'Offline', 'Levels']
  },
  {
    id: 'ai-sentence-builder',
    name: 'Sentence Builder',
    description: 'Arrange words to form sentences - Grammar practice',
    icon: MessageSquare,
    color: 'purple',
    bgColor: 'bg-purple-500/10',
    iconColor: 'text-purple-400',
    path: '/games/ai-sentence-builder/index.html',
    features: ['TTS Support', 'Leaderboard', 'Offline', 'Levels']
  }
];

const TeacherFunLearningPage = () => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeGame, setActiveGame] = useState(null);
  const [selectedGameFilter, setSelectedGameFilter] = useState('all');
  const [selectedClassFilter, setSelectedClassFilter] = useState('all');
  const [updatingClass, setUpdatingClass] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [permRes, scoresRes] = await Promise.all([
        api.get('/fun-learning/permissions'),
        api.get('/fun-learning/scores')
      ]);
      setPermissions(permRes.data);
      setLeaderboard(scoresRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      let params = [];
      if (selectedGameFilter !== 'all') params.push(`game=${selectedGameFilter}`);
      if (selectedClassFilter !== 'all') params.push(`class_id=${selectedClassFilter}`);
      const queryStr = params.length > 0 ? `?${params.join('&')}` : '';
      const res = await api.get(`/fun-learning/scores${queryStr}`);
      setLeaderboard(res.data);
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
    }
  };

  useEffect(() => {
    fetchLeaderboard();
  }, [selectedGameFilter, selectedClassFilter]);

  const togglePermission = async (classId, currentEnabled) => {
    setUpdatingClass(classId);
    try {
      await api.put(`/fun-learning/permissions/${classId}`, {
        enabled: !currentEnabled
      });
      setPermissions(prev => prev.map(p => 
        p.id === classId ? { ...p, enabled: !currentEnabled ? 1 : 0 } : p
      ));
      toast.success(`Fun Learning ${!currentEnabled ? 'enabled' : 'disabled'} for class`);
    } catch (err) {
      toast.error('Failed to update permission');
    } finally {
      setUpdatingClass(null);
    }
  };

  // Handle postMessage from games (play mode - no score saving)
  const handleGameMessage = useCallback((event) => {
    if (event.data && event.data.type === 'GAME_SCORE') {
      const { score } = event.data;
      toast.info(`Play mode: Score ${score} (not saved to leaderboard)`);
    } else if (event.data && event.data.type === 'GAME_CLOSE') {
      setActiveGame(null);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('message', handleGameMessage);
    return () => window.removeEventListener('message', handleGameMessage);
  }, [handleGameMessage]);

  const openGame = async (game) => {
    setActiveGame(game);
    try {
      await document.documentElement.requestFullscreen();
    } catch (err) {
      // Fullscreen not supported or denied — game still works in overlay mode
    }
  };

  const closeGame = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
    setActiveGame(null);
  };

  const formatTime = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return <Medal className="h-5 w-5 text-yellow-400" />;
    if (rank === 2) return <Medal className="h-5 w-5 text-slate-300" />;
    if (rank === 3) return <Medal className="h-5 w-5 text-amber-600" />;
    return <span className="text-slate-400 font-medium">{rank}</span>;
  };

  const enabledCount = permissions.filter(p => p.enabled).length;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" />
        </div>
      </DashboardLayout>
    );
  }

  // Game fullscreen view
  if (activeGame) {
    return (
      <div className="fixed inset-0 z-50 bg-slate-900 flex flex-col">
        {/* Game Header */}
        <div className="flex items-center justify-between p-4 bg-slate-800 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <activeGame.icon className={`h-6 w-6 ${activeGame.iconColor}`} />
            <span className="text-white font-semibold">{activeGame.name}</span>
            <Badge variant="secondary" className="bg-amber-500/20 text-amber-300">
              <Star className="h-3 w-3 mr-1" />
              Play Mode - Scores not saved
            </Badge>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={closeGame}
            className="text-slate-400 hover:text-white"
          >
            <X className="h-5 w-5 mr-1" />
            Close Game
          </Button>
        </div>
        
        {/* Game iframe */}
        <div className="flex-1">
          <iframe
            src={activeGame.path}
            className="w-full h-full border-0"
            title={activeGame.name}
            allow="fullscreen"
          />
        </div>
      </div>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
              <Gamepad2 className="h-8 w-8 text-violet-500" />
              Fun Learning
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Manage games and view student progress
            </p>
          </div>
          <Badge variant="secondary" className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
            <CheckCircle className="h-4 w-4 mr-1" />
            {enabledCount} of {permissions.length} classes enabled
          </Badge>
        </div>

        <Tabs defaultValue="settings" className="space-y-4">
          <TabsList>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Class Settings
            </TabsTrigger>
            <TabsTrigger value="games" className="flex items-center gap-2">
              <Gamepad2 className="h-4 w-4" />
              Play Games
            </TabsTrigger>
            <TabsTrigger value="leaderboard" className="flex items-center gap-2">
              <Trophy className="h-4 w-4" />
              Leaderboard
            </TabsTrigger>
          </TabsList>

          {/* Class Settings Tab */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <School className="h-5 w-5" />
                  Class Permissions
                </CardTitle>
                <CardDescription>
                  Enable or disable Fun Learning for your assigned classes
                </CardDescription>
              </CardHeader>
              <CardContent>
                {permissions.length === 0 ? (
                  <div className="text-center py-8">
                    <School className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No classes assigned to you</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {permissions.map((cls) => (
                      <div 
                        key={cls.id}
                        className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
                          cls.enabled 
                            ? 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800/50'
                            : 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${cls.enabled ? 'bg-emerald-500/20' : 'bg-slate-200 dark:bg-slate-700'}`}>
                            <School className={`h-5 w-5 ${cls.enabled ? 'text-emerald-500' : 'text-slate-500'}`} />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">{cls.name}</p>
                            <p className="text-sm text-slate-500">
                              {cls.enabled ? (
                                <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                                  <CheckCircle className="h-3 w-3" />
                                  Fun Learning enabled
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-slate-500">
                                  <XCircle className="h-3 w-3" />
                                  Fun Learning disabled
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-slate-500">
                            {cls.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                          <Switch
                            checked={!!cls.enabled}
                            onCheckedChange={() => togglePermission(cls.id, cls.enabled)}
                            disabled={updatingClass === cls.id}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Games Tab */}
          <TabsContent value="games">
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50">
                <div className="flex items-center gap-2 mb-1">
                  <Star className="h-5 w-5 text-amber-500" />
                  <span className="font-medium text-amber-700 dark:text-amber-300">Play Mode</span>
                </div>
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  As a teacher, you can play all games but your scores will not be saved to the leaderboard.
                </p>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {GAMES.map((game) => {
                  const Icon = game.icon;
                  return (
                    <Card 
                      key={game.id}
                      className={`cursor-pointer transition-all duration-300 hover:shadow-lg hover:border-${game.color}-500/50 group overflow-hidden`}
                      onClick={() => openGame(game)}
                    >
                      <CardContent className="p-6 relative">
                        <div className={`inline-flex p-4 rounded-xl ${game.bgColor} mb-4 transition-transform group-hover:scale-110`}>
                          <Icon className={`h-8 w-8 ${game.iconColor}`} />
                        </div>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                          {game.name}
                        </h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                          {game.description}
                        </p>
                        <Button variant="ghost" className="w-full">
                          <Play className="h-4 w-4 mr-2" />
                          Play Now
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-yellow-500" />
                    Fun Learning Grades
                  </CardTitle>
                  <div className="flex gap-2">
                    <Select value={selectedClassFilter} onValueChange={setSelectedClassFilter}>
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="All Classes" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Classes</SelectItem>
                        {permissions.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={selectedGameFilter} onValueChange={setSelectedGameFilter}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="All Games" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Games</SelectItem>
                        {GAMES.map(g => (
                          <SelectItem key={g.id} value={g.name}>{g.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {leaderboard.length === 0 ? (
                  <div className="text-center py-8">
                    <Trophy className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-500">No scores yet</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[400px]">
                    <table className="w-full">
                      <thead className="sticky top-0 bg-white dark:bg-slate-900">
                        <tr className="border-b border-slate-200 dark:border-slate-700">
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Rank</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Student Name</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Class</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Game</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Score</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Time</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leaderboard.map((entry, idx) => (
                          <tr key={entry.id} className="border-b border-slate-100 dark:border-slate-800">
                            <td className="py-3 px-4">
                              <div className="w-8 h-8 flex items-center justify-center">
                                {getRankBadge(idx + 1)}
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <span className="font-medium text-slate-900 dark:text-white">
                                {entry.student_name}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <Badge variant="secondary">{entry.class_name}</Badge>
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-slate-600 dark:text-slate-300">{entry.game_name}</span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <span className="font-bold text-violet-600 dark:text-violet-400">{entry.score}</span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <span className="text-slate-500 flex items-center justify-end gap-1">
                                <Clock className="h-3 w-3" />
                                {formatTime(entry.time_taken)}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <span className="text-slate-500 flex items-center justify-end gap-1">
                                <Calendar className="h-3 w-3" />
                                {formatDate(entry.created_at)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default TeacherFunLearningPage;
